import { useParams, Link } from "react-router-dom";
import { CheckCircle } from "lucide-react";
import SEO from "../components/SEO";
import { imgUrl } from "../lib/api";
import { getOrderSnapshot } from "../lib/orderSnapshot";

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const order = getOrderSnapshot(orderNumber);

  return (
    <div className="max-w-2xl mx-auto px-4 py-20" data-testid="order-success-page">
      <SEO title="Order Confirmed" description="Thank you for your order — Humaira Crafts" />
      <div className="text-center">
        <CheckCircle strokeWidth={1} size={64} className="text-gold mx-auto" />
        <h1 className="text-4xl tracking-tighter font-semibold mt-8">Order Confirmed</h1>
        <p className="font-serif text-3xl text-gold mt-3" data-testid="order-number">{orderNumber}</p>
        <p className="text-muted-foreground mt-3 text-sm">A confirmation email has been sent to you. We will notify you as your order progresses.</p>
      </div>

      {order && (
        <div className="mt-10 bg-white border border-black/10 p-6 md:p-8 text-left" data-testid="order-summary">
          <p className="small-caps text-gold mb-5">Order Summary</p>
          <div className="flex flex-col gap-4">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex items-center gap-4">
                {i.image && <img src={imgUrl(i.image)} alt={i.name} className="w-14 h-14 object-cover border border-black/10" />}
                <p className="flex-1 text-sm">{i.name} <span className="text-muted-foreground">× {i.qty}</span></p>
                <p className="text-sm font-medium">₹{(i.price * i.qty).toLocaleString("en-IN")}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-black/10 mt-5 pt-4 flex flex-col gap-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>₹{order.subtotal.toLocaleString("en-IN")}</span></div>
            {order.discount > 0 && (
              <div className="flex justify-between text-green-800"><span>Discount{order.coupon_code ? ` (${order.coupon_code})` : ""}</span><span>−₹{order.discount.toLocaleString("en-IN")}</span></div>
            )}
            <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>Confirmed after order</span></div>
            <div className="flex justify-between font-semibold text-base pt-2 mt-1 border-t border-black/10"><span>Total</span><span data-testid="order-total">₹{order.total.toLocaleString("en-IN")}</span></div>
          </div>
          <div className="border-t border-black/10 mt-5 pt-4 text-sm grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Delivery To</p>
              <p>{order.customer.name}</p>
              <p className="text-muted-foreground">{order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ""}, {order.address.city}, {order.address.state} — {order.address.pincode}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Payment</p>
              <p>{order.payment_method === "cod" ? "Cash on Delivery" : "Paid Online"}</p>
              <p className="text-muted-foreground mt-1">Status: <span className="text-gold">Order Placed</span></p>
            </div>
          </div>
        </div>
      )}

      {order?.account_created && (
        <div className="mt-10 bg-gold-light border border-gold/40 p-6 text-left" data-testid="account-created-note">
          <p className="font-serif text-xl">Your account is ready</p>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            We created your Humaira Crafts account and signed you in — this order, your delivery address and your wishlist are saved to it permanently, on every device. To sign in again later, use "Forgot your password?" on the login page and we'll email you a secure link to set your password.
          </p>
          <Link to="/account" className="inline-block mt-3 small-caps text-gold border-b border-gold" data-testid="view-account-link">View My Account</Link>
        </div>
      )}
      {order && !order.account_created && order.account_linked && (
        <p className="text-xs text-muted-foreground mt-8" data-testid="account-linked-note">This order has been added to your existing Humaira Crafts account — sign in anytime to view your full order history.</p>
      )}

      <div className="flex flex-wrap justify-center gap-4 mt-10">
        <Link to={`/track-order?order=${orderNumber}${order ? `&email=${encodeURIComponent(order.customer.email)}` : ""}`} className="bg-[#111] text-[#FAF9F6] px-8 py-3 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300" data-testid="track-order-link">
          View / Track Order
        </Link>
        <Link to="/shop" className="border border-[#111] px-8 py-3 small-caps hover:bg-[#111] hover:text-[#FAF9F6] transition-colors duration-300" data-testid="continue-shopping-link">
          Continue Shopping
        </Link>
      </div>
      {!order && (
        <p className="text-center text-xs text-muted-foreground mt-6">Use your order number and checkout email anytime on the Track Order page.</p>
      )}
    </div>
  );
}
